/* Desayunos del Cielo · product gallery (scroll-snap carousel, no dependencies). */
if (!customElements.get('ddc-product-gallery')) {
  customElements.define(
    'ddc-product-gallery',
    class DdcProductGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-pg-track]');
        this.slides = Array.from(this.querySelectorAll('[data-pg-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-pg-thumb]'));
        this.prev = this.querySelector('[data-pg-prev]');
        this.next = this.querySelector('[data-pg-next]');
        this.current = this.querySelector('[data-pg-current]');
        if (!this.track) return;
        this.index = 0;
        this.setupZoom();
        if (this.slides.length < 2) return;

        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.prev?.addEventListener('click', () => this.goTo(this.index - 1));
        this.next?.addEventListener('click', () => this.goTo(this.index + 1));
        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.pgThumb)));
        });
        this.track.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.goTo(this.index + 1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.goTo(this.index - 1);
          }
        });

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const index = this.slides.indexOf(entry.target);
                this.hydrateSlide(index);
                if (entry.intersectionRatio < 0.6) return;
                this.setActive(this.slides.indexOf(entry.target));
              }
            });
          },
          { root: this.track, threshold: [0.01, 0.6] }
        );
        this.slides.forEach((slide) => this.observer.observe(slide));
        /* Slide 2 is deferred for LCP; warm it on first interaction or after load. */
        const warmNext = () => this.hydrateAround(this.index > 0 ? this.index : 0);
        this.track.addEventListener('pointerdown', warmNext, { once: true, passive: true });
        this.addEventListener('focusin', warmNext, { once: true });
        if (document.readyState === 'complete') setTimeout(warmNext, 1200);
        else window.addEventListener('load', () => setTimeout(warmNext, 1200), { once: true });
        this.classList.add('is-enhanced');
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      /* ---------- Visor a pantalla completa (2026-10-29) ----------
         Tocar o hacer clic en cualquier foto abre la foto grande (object-fit: contain), con
         deslizamiento entre fotos, botón de cerrar, Escape, flechas del teclado, bloqueo del
         scroll de fondo y foco devuelto al cerrar. Sin librerías. */
      setupZoom() {
        const zoomButton = this.querySelector('[data-pg-zoom]');
        let start = null;
        this.track.addEventListener('pointerdown', (event) => {
          start = { x: event.clientX, y: event.clientY, t: Date.now() };
        }, { passive: true });
        this.track.addEventListener('click', (event) => {
          const slide = event.target.closest('[data-pg-slide]');
          if (!slide) return;
          if (start && (Math.abs(event.clientX - start.x) > 10 || Math.abs(event.clientY - start.y) > 10)) return;
          this.openViewer(this.slides.indexOf(slide), slide);
        });
        this.track.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.openViewer(this.index || 0, this.track);
          }
        });
        zoomButton?.addEventListener('click', () => this.openViewer(this.index || 0, zoomButton));
        this.slides.forEach((slide) => slide.classList.add('is-zoomable'));
      }

      buildViewer() {
        if (this.viewer) return this.viewer;
        const v = document.createElement('div');
        v.className = 'ddc-pgz';
        v.hidden = true;
        v.setAttribute('role', 'dialog');
        v.setAttribute('aria-modal', 'true');
        v.setAttribute('aria-label', this.track.getAttribute('aria-label') || 'Fotos del producto');
        const multi = this.slides.length > 1;
        v.innerHTML = `
          <div class="ddc-pgz__bar">
            <span class="ddc-pgz__count" aria-live="polite" data-pgz-count></span>
            <button type="button" class="ddc-pgz__close" aria-label="Cerrar fotos" data-pgz-close>
              <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="22" height="22"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>
            </button>
          </div>
          <div class="ddc-pgz__track" data-pgz-track></div>
          ${multi ? `<button type="button" class="ddc-pgz__arrow ddc-pgz__arrow--prev" aria-label="Foto anterior" data-pgz-prev><svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="22" height="22"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
          <button type="button" class="ddc-pgz__arrow ddc-pgz__arrow--next" aria-label="Foto siguiente" data-pgz-next><svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="22" height="22"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>` : ''}`;
        const track = v.querySelector('[data-pgz-track]');
        this.slides.forEach((slide, i) => {
          const fig = document.createElement('figure');
          fig.className = 'ddc-pgz__slide';
          const img = document.createElement('img');
          img.className = 'ddc-pgz__img';
          img.alt = slide.dataset.pgAlt || '';
          img.decoding = 'async';
          img.dataset.src = slide.dataset.pgFull || (slide.querySelector('img')?.currentSrc || slide.querySelector('img')?.src || '');
          fig.appendChild(img);
          fig.setAttribute('aria-label', `${i + 1} de ${this.slides.length}`);
          track.appendChild(fig);
        });
        document.body.appendChild(v);
        this.viewer = v;
        this.vTrack = track;
        this.vSlides = Array.from(track.children);
        this.vCount = v.querySelector('[data-pgz-count]');
        v.querySelector('[data-pgz-close]').addEventListener('click', () => this.closeViewer());
        v.querySelector('[data-pgz-prev]')?.addEventListener('click', () => this.viewerGo(this.vIndex - 1));
        v.querySelector('[data-pgz-next]')?.addEventListener('click', () => this.viewerGo(this.vIndex + 1));
        v.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') { event.preventDefault(); this.closeViewer(); }
          else if (event.key === 'ArrowRight') { event.preventDefault(); this.viewerGo(this.vIndex + 1); }
          else if (event.key === 'ArrowLeft') { event.preventDefault(); this.viewerGo(this.vIndex - 1); }
          else if (event.key === 'Tab') {
            const focusables = Array.from(v.querySelectorAll('button')).filter((b) => b.offsetParent !== null);
            if (!focusables.length) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
          }
        });
        let raf = 0;
        track.addEventListener('scroll', () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
            if (i !== this.vIndex) this.viewerSet(i);
          });
        }, { passive: true });
        return v;
      }

      loadViewerImage(i) {
        const img = this.vSlides?.[i]?.querySelector('img[data-src]');
        if (!img) return;
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }

      viewerSet(i) {
        const last = this.vSlides.length - 1;
        this.vIndex = Math.max(0, Math.min(last, i));
        this.loadViewerImage(this.vIndex);
        this.loadViewerImage(this.vIndex + 1);
        this.loadViewerImage(this.vIndex - 1);
        if (this.vCount) this.vCount.textContent = this.vSlides.length > 1 ? `${this.vIndex + 1} / ${this.vSlides.length}` : '';
        const prev = this.viewer.querySelector('[data-pgz-prev]');
        const next = this.viewer.querySelector('[data-pgz-next]');
        if (prev) prev.disabled = this.vIndex === 0;
        if (next) next.disabled = this.vIndex === last;
      }

      viewerGo(i) {
        const last = this.vSlides.length - 1;
        const target = Math.max(0, Math.min(last, i));
        this.vTrack.scrollTo({ left: target * this.vTrack.clientWidth, behavior: 'auto' });
        this.viewerSet(target);
      }

      openViewer(index, opener) {
        const v = this.buildViewer();
        if (!v.hidden) return;
        this.viewerOpener = opener || document.activeElement;
        this.savedScroll = window.scrollY;
        document.documentElement.classList.add('ddc-pgz-lock');
        document.body.classList.add('ddc-pgz-open');
        v.hidden = false;
        this.vIndex = -1;
        requestAnimationFrame(() => {
          this.vTrack.scrollTo({ left: index * this.vTrack.clientWidth, behavior: 'auto' });
          this.viewerSet(index);
          v.classList.add('is-open');
          v.querySelector('[data-pgz-close]').focus({ preventScroll: true });
        });
        try {
          if (typeof window.gtag === 'function') window.gtag('event', 'ddc_photo_zoom', { foto: index + 1 });
        } catch (_) {}
      }

      closeViewer() {
        if (!this.viewer || this.viewer.hidden) return;
        const back = this.vIndex;
        this.viewer.classList.remove('is-open');
        this.viewer.hidden = true;
        document.documentElement.classList.remove('ddc-pgz-lock');
        document.body.classList.remove('ddc-pgz-open');
        if (typeof this.savedScroll === 'number' && Math.abs(window.scrollY - this.savedScroll) > 2) window.scrollTo(0, this.savedScroll);
        if (this.slides.length > 1 && back >= 0 && back !== this.index) this.goTo(back);
        const opener = this.viewerOpener;
        if (opener && typeof opener.focus === 'function') {
          if (!opener.hasAttribute('tabindex') && !/^(BUTTON|A|INPUT|SELECT|TEXTAREA)$/.test(opener.tagName)) {
            (this.track.hasAttribute('tabindex') ? this.track : this.querySelector('[data-pg-zoom]'))?.focus({ preventScroll: true });
          } else {
            opener.focus({ preventScroll: true });
          }
        }
      }

      goTo(target) {
        const index = Math.max(0, Math.min(this.slides.length - 1, target));
        this.hydrateAround(index);
        this.track.scrollTo({
          left: index * this.track.clientWidth,
          behavior: this.reduceMotion.matches ? 'auto' : 'smooth',
        });
        this.setActive(index);
      }

      hydrateSlide(index) {
        const image = this.slides[index]?.querySelector('img[data-src]');
        if (!image) return;
        if (image.dataset.srcset) image.srcset = image.dataset.srcset;
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
        image.removeAttribute('data-srcset');
      }

      hydrateAround(index) {
        this.hydrateSlide(index - 1);
        this.hydrateSlide(index);
        this.hydrateSlide(index + 1);
      }

      setActive(index) {
        if (index < 0 || index === this.index) {
          this.updateControls();
          return;
        }
        this.index = index;
        this.updateControls();
      }

      updateControls() {
        const last = this.slides.length - 1;
        if (this.prev) this.prev.disabled = this.index === 0;
        if (this.next) this.next.disabled = this.index === last;
        if (this.current) this.current.textContent = String(this.index + 1);
        this.thumbs.forEach((thumb, i) => {
          if (i === this.index) {
            thumb.setAttribute('aria-current', 'true');
            const list = thumb.closest('[data-pg-thumbs]');
            if (list && list.scrollWidth > list.clientWidth + 1) {
              const item = thumb.parentElement || thumb;
              list.scrollTo({ left: item.offsetLeft - list.clientWidth / 2 + item.clientWidth / 2 });
            }
          } else {
            thumb.removeAttribute('aria-current');
          }
        });
      }
    }
  );
}
